import { FC, useEffect, useMemo, useRef, useState } from 'react'
import moment from 'moment'
import { ReactComponent as ReloadIcon } from 'src/assets/svg/Reload.svg'
import { ReactComponent as AiSparkleIcon } from 'src/assets/svg/ai-sparkle.svg'
import { ReactComponent as InfoIcon } from 'src/assets/svg/ri-information-fill.svg'
import { NoDataModal } from 'src/components/NoDataModal'
import { Skeleton } from 'src/components/Skeleton'
import { TimeFilterModal } from 'src/components/TimeFilterModal'
import { CalendarContext } from 'src/utils/calendarContext'
import {
  AISummaryGenerateData,
  AISummaryListData,
  AISummaryRange,
  GenerateAISummaryPayload,
} from 'src/interfaces/aiSummary.interface'
import {
  aiSummaryRangePayload,
  pickedAISummaryRange,
} from 'src/utils/aiSummaryRange'
import styles from './aiSummaryWidget.module.scss'

/**
 * The AI "Summary" card shared by Content Analysis, Review Analysis and Social
 * Listening: header with the AI badge, "Generate New Summary" + 24h countdown,
 * one tab per stored period (newest first) and the prose for the active tab.
 *
 * Presentational — each module passes its own list query / generate mutation
 * (see SummarySection, ScopedSummarySection) and its own wording.
 *
 * Independent of the page's global date filter by design (all three
 * widgets): nothing is pre-selected. "Generate New Summary" opens the widget's
 * own Time Filter, where the user picks an explicit start and end date. Every
 * stored period is a tab, newest first.
 *
 * Limits come from the API (sm.back service/periodSummary.js) and are the same
 * for every widget: one summary per analysis / campaign / facility per rolling
 * 24 hours ("You can generate one summary per day"). `nextAvailableAt` drives
 * the countdown and keeps the button disabled until then.
 */
export interface AISummaryWidgetCopy {
  emptyText: string
  footerText: string
  generatingText: string
}

interface AISummaryWidgetProps {
  calendar?: CalendarContext
  copy: AISummaryWidgetCopy
  data?: AISummaryListData
  isError: boolean
  isGenerating: boolean
  isLoading: boolean
  // Resolves with the generate response, or undefined when the request failed
  // (the mutation hook has already shown the error).
  onGenerate: (
    payload: GenerateAISummaryPayload
  ) => Promise<AISummaryGenerateData | undefined>
  onRetry: () => void
}

const DocumentIcon: FC = () => (
  <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M14 2v6h6M9 13h2M9 17h2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const ClockIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <circle cx="8" cy="8" r="6.25" stroke="#86949D" strokeWidth="1.5" />
    <path
      d="M8 4.5V8l2.25 1.5"
      stroke="#86949D"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const ChevronLeftIcon: FC = () => (
  <svg fill="none" height="16" viewBox="0 0 16 16" width="16">
    <path
      d="M10 3.5 5.5 8l4.5 4.5"
      stroke="#fff"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.75"
    />
  </svg>
)

// Real dimensions from summarySection.module.scss. The loading view mirrors the
// READY view (.generating > .tabsBar + .summaryBody), not the .content shell the
// empty/error views use — .content is min-height 328px while the ready view is
// 24 + 36 + 300 + 24 = 384px, so loading through .content collapsed the widget by
// ~56px and it jumped open when the summaries landed.
// .summaryBody itself is a FIXED height: 300px, so only its inner text is
// skeletoned — that box cannot shift.
const TAB_HEIGHT = 36 // .tab height
const TAB_WIDTH = 188 // .tabActive min-width (one tab is always active)
const HEADING_HEIGHT = 16 // .summaryHeading 14px/500
const HEADING_WIDTH = 220
const TEXT_LINE_HEIGHT = 24 // .summaryText 16px * 1.5 line-height
const TEXT_LINE_WIDTHS = ['100%', '100%', '92%', '64%']

const formatCountdown = (totalMs: number) => {
  const totalSeconds = Math.max(0, Math.floor(totalMs / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return [hours, minutes, seconds]
    .map((n) => String(n).padStart(2, '0'))
    .join(':')
}

export const AISummaryWidget: FC<AISummaryWidgetProps> = ({
  calendar,
  copy,
  data,
  isError,
  isGenerating,
  isLoading,
  onGenerate,
  onRetry,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  // The period the last generate asked for — the "no data" modal names it.
  const [noDataRange, setNoDataRange] = useState<AISummaryRange | null>(null)
  // The tab the user clicked; null = the newest summary.
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [canScrollTabs, setCanScrollTabs] = useState(false)
  const [nowTs, setNowTs] = useState(() => Date.now())
  const tabsRef = useRef<HTMLDivElement>(null)

  // Newest first. The endpoint sorts createdAt ascending, which painted the
  // tabs oldest-to-newest and left the current week off at the right-hand end.
  const summaries = useMemo(
    () => [...(data?.summaries ?? [])].reverse(),
    [data?.summaries]
  )

  const nextAvailableAt = data?.nextAvailableAt ?? null
  const remainingMs = nextAvailableAt
    ? new Date(nextAvailableAt).getTime() - nowTs
    : 0
  const isCoolingDown = remainingMs > 0
  const isBusy = isGenerating || isCoolingDown

  // Selection is derived, not synced in an effect: a clicked tab wins while it
  // still exists, otherwise the newest summary is shown.
  const activeSummary =
    (pickedId && summaries.find((summary) => summary.id === pickedId)) ||
    summaries[0]

  let view: 'loading' | 'error' | 'generating' | 'ready' | 'empty'
  if (isGenerating) view = 'generating'
  else if (isLoading) view = 'loading'
  else if (isError) view = 'error'
  else if (summaries.length > 0) view = 'ready'
  else view = 'empty'

  // Tick the "Next available in" countdown once per second while cooling down.
  useEffect(() => {
    if (!isCoolingDown) return undefined
    const timer = setInterval(() => setNowTs(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [isCoolingDown])

  // A new cooldown from the server must start from the current time, not the
  // last tick (which stops while nothing is cooling down).
  useEffect(() => {
    setNowTs(Date.now())
  }, [nextAvailableAt])

  // When the 24h countdown runs out, ask the server again before trusting the
  // re-enabled button (the client clock can be off, and another tab may have
  // generated meanwhile).
  const wasCoolingDown = useRef(isCoolingDown)
  useEffect(() => {
    if (wasCoolingDown.current && !isCoolingDown && nextAvailableAt) onRetry()
    wasCoolingDown.current = isCoolingDown
  }, [isCoolingDown, nextAvailableAt, onRetry])

  // Only show the scroll control when the tabs actually overflow.
  useEffect(() => {
    const el = tabsRef.current
    if (!el) return undefined
    const check = () => setCanScrollTabs(el.scrollWidth > el.clientWidth + 1)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [view, summaries.length])

  const scrollTabs = (direction: number) => {
    tabsRef.current?.scrollBy({ behavior: 'smooth', left: direction * 220 })
  }

  // Picked dates keep the user's local day boundaries and offsets (DST too).
  const handlePickedRange = (from: moment.Moment, to: moment.Moment) => {
    setIsModalOpen(false)
    const target = pickedAISummaryRange(from, to)
    // Let the fresh summary open once it lands.
    setPickedId(null)
    onGenerate(aiSummaryRangePayload(target)).then((result) => {
      if (result && !result.hasData) setNoDataRange(target)
    })
  }

  const renderGenerateButton = () => (
    <button
      className={styles.generateButton}
      disabled={isBusy}
      onClick={() => setIsModalOpen(true)}
      type="button"
    >
      Generate New Summary
    </button>
  )

  return (
    <div className={styles.summaryWidget}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.summaryLabel}>Summary</span>
          <span className={styles.aiBadge}>
            <AiSparkleIcon className={styles.aiBadgeIcon} />
            <span className={styles.aiBadgeText}>AI-generated</span>
          </span>
        </div>
        <div className={styles.headerRight}>
          {isCoolingDown && (
            <span className={styles.nextAvailable}>
              Next available in{' '}
              <span className={styles.countdown}>
                {formatCountdown(remainingMs)}
              </span>
              <ClockIcon />
            </span>
          )}
          {renderGenerateButton()}
        </div>
      </div>

      {view === 'loading' ? (
        <div className={styles.generating}>
          <div className={styles.tabsBar}>
            <Skeleton
              height={TAB_HEIGHT}
              radius="8px 8px 0 0"
              width={TAB_WIDTH}
            />
          </div>
          <div className={styles.summaryBody}>
            <div className={styles.summaryBodyInner}>
              <Skeleton height={HEADING_HEIGHT} width={HEADING_WIDTH} />
              {TEXT_LINE_WIDTHS.map((width, index) => (
                <Skeleton
                  height={TEXT_LINE_HEIGHT}
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  width={width}
                />
              ))}
            </div>
          </div>
        </div>
      ) : view === 'error' ? (
        <div className={styles.content}>
          <div className={styles.emptyInner}>
            <h3 className={styles.emptyTitle}>Couldn&apos;t load summaries</h3>
            <p className={styles.emptyText}>
              Something went wrong while loading your summaries.
            </p>
            <button
              className={styles.generateButton}
              onClick={onRetry}
              type="button"
            >
              Try Again
            </button>
          </div>
        </div>
      ) : view === 'empty' ? (
        <div className={styles.content}>
          <div className={styles.emptyInner}>
            <div className={styles.emptyIconWrapper}>
              <DocumentIcon />
            </div>
            <h3 className={styles.emptyTitle}>No Summary Generated Yet</h3>
            <p className={styles.emptyText}>{copy.emptyText}</p>
            {renderGenerateButton()}
          </div>
        </div>
      ) : (
        <div className={styles.generating}>
          <div className={styles.tabsBar}>
            {canScrollTabs && (
              <button
                className={styles.tabsScrollButton}
                onClick={() => scrollTabs(-1)}
                type="button"
              >
                <ChevronLeftIcon />
              </button>
            )}
            <div className={styles.tabsScroll} ref={tabsRef}>
              {summaries.map((summary) => (
                <button
                  className={`${styles.tab} ${
                    summary.id === activeSummary?.id ? styles.tabActive : ''
                  }`}
                  key={summary.id}
                  onClick={() => setPickedId(summary.id)}
                  type="button"
                >
                  {summary.periodLabel}
                </button>
              ))}
            </div>
          </div>

          {view === 'generating' ? (
            <div className={styles.generatingBody}>
              <ReloadIcon className={styles.generatingIcon} />
              <h3 className={styles.generatingTitle}>
                We&apos;re Generating Your Summary...
              </h3>
              <p className={styles.generatingText}>{copy.generatingText}</p>
            </div>
          ) : (
            activeSummary && (
              <div className={styles.summaryBody}>
                <div className={styles.summaryBodyInner}>
                  <h4 className={styles.summaryHeading}>
                    {activeSummary.heading}
                  </h4>
                  {activeSummary.paragraphs.map((paragraph, index) => (
                    <p
                      className={styles.summaryText}
                      // Two paragraphs can open with the same words.
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${index}-${paragraph.slice(0, 24)}`}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}

      <div className={styles.footer}>
        <InfoIcon className={styles.footerIcon} />
        <span className={styles.footerText}>{copy.footerText}</span>
      </div>

      {isModalOpen && (
        <TimeFilterModal
          calendar={calendar}
          onClose={() => setIsModalOpen(false)}
          onGenerate={handlePickedRange}
        />
      )}

      <NoDataModal
        fromDate={noDataRange ? moment(noDataRange.fromDay) : null}
        isOpen={!!noDataRange}
        onClose={() => setNoDataRange(null)}
        toDate={noDataRange ? moment(noDataRange.toDay) : null}
      />
    </div>
  )
}
